import {
	APIError,
	type SignInResult,
	beginResetPassword as apiBeginResetPassword,
	completeResetPassword as apiCompleteResetPassword,
	confirmEnrollSmsMfa as apiConfirmEnrollSmsMfa,
	confirmEnrollTotpMfa as apiConfirmEnrollTotpMfa,
	enableEmailMfa as apiEnableEmailMfa,
	enrollSmsMfa as apiEnrollSmsMfa,
	enrollTotpMfa as apiEnrollTotpMfa,
	refresh as apiRefresh,
	signIn as apiSignIn,
	signOut as apiSignOut,
	verifyMfa as apiVerifyMfa,
} from '@clients/FolxAPIv2';
import { datadogRum } from '@datadog/browser-rum';
import { CognitoUser } from 'amazon-cognito-identity-js';
import type { Auth as AuthClass } from 'aws-amplify';
import axios from 'axios';
import { User as ConfigCatUser, useConfigCatClient } from 'configcat-react';
import React, { FC, useContext, useEffect, useReducer, useState } from 'react';

import { ApiEndpoints } from '@utils/endpoints';

export const isBrowser = typeof window !== `undefined`;

type Session = {
	accessToken: string;
	idToken: string;
	refreshToken: string;
	username: string;
	email: string;
};

const REFRESH_BUFFER_SECONDS = 30;

const SESSION_STORAGE_KEY = 'folx_api_v2_session';

type IdTokenClaims = {
	email?: string;
	email_verified?: boolean;
	exp?: number;
};

const decodeIdTokenClaims = (idToken: string): IdTokenClaims => {
	try {
		const payload = idToken.split('.')[1];
		return JSON.parse(atob(payload.replaceAll('-', '+').replaceAll('_', '/'))) as IdTokenClaims;
	} catch {
		return {};
	}
};

const readStoredSession = (): Session | undefined => {
	if (!isBrowser) return undefined;
	try {
		const raw = window.sessionStorage.getItem(SESSION_STORAGE_KEY);
		return raw ? (JSON.parse(raw) as Session) : undefined;
	} catch {
		return undefined;
	}
};

const writeStoredSession = (session: Session | undefined) => {
	if (!isBrowser) return;
	if (session) {
		window.sessionStorage.setItem(SESSION_STORAGE_KEY, JSON.stringify(session));
	} else {
		window.sessionStorage.removeItem(SESSION_STORAGE_KEY);
	}
};

let currentSession: Session | undefined = readStoredSession();
const sessionListeners = new Set<(session: Session | undefined) => void>();

const commitSession = (session: Session | undefined) => {
	currentSession = session;
	writeStoredSession(session);
	sessionListeners.forEach((listener) => listener(session));
};

let refreshPromise: Promise<Session | undefined> | null = null;

const refreshSession = (): Promise<Session | undefined> => {
	if (!currentSession) return Promise.resolve(undefined);
	if (refreshPromise) return refreshPromise;

	const sessionBeingRefreshed = currentSession;

	refreshPromise = (async () => {
		try {
			const result = await apiRefresh(sessionBeingRefreshed.refreshToken);
			if (!result.accessToken || !result.idToken) {
				throw new Error('refresh did not return a complete session');
			}

			const newSession: Session = {
				...sessionBeingRefreshed,
				accessToken: result.accessToken,
				idToken: result.idToken,
			};
			commitSession(newSession);
			return newSession;
		} catch (e) {
			console.error('Error refreshing session', e);
			commitSession(undefined);
			return undefined;
		} finally {
			refreshPromise = null;
		}
	})();

	return refreshPromise;
};

const getSessionAuthToken = async (): Promise<string | undefined> => {
	if (!currentSession) return undefined;

	const { exp } = decodeIdTokenClaims(currentSession.idToken);
	const nowSeconds = Date.now() / 1000;

	if (exp !== undefined && exp - REFRESH_BUFFER_SECONDS <= nowSeconds) {
		const refreshed = await refreshSession();
		return refreshed?.idToken;
	}

	return currentSession.idToken;
};

const { Auth }: { Auth: typeof AuthClass } = isBrowser ? require('aws-amplify') : { Auth: {} };

type User = CognitoUser;

interface IAuthContext {
	user?: User;
	updateUser: (user?: User) => void;
	initialized: boolean;
	session?: Session;
	updateSession: (session?: Session) => void;
}

const AuthContext = React.createContext<IAuthContext>({
	updateUser: (user) => {},
	initialized: false,
	updateSession: commitSession,
});

export const AuthProvider: FC<{ children: React.ReactNode }> = ({ children }) => {
	const configCat = useConfigCatClient();

	const [session, setSession] = useState<Session | undefined>(() => currentSession);

	useEffect(() => {
		sessionListeners.add(setSession);
		return () => {
			sessionListeners.delete(setSession);
		};
	}, []);

	const [{ user, initialized }, updateUser] = useReducer(
		({ user }: { user: User | undefined; initialized: boolean }, newUser: User | undefined) => {
			if (newUser && !newUser?.getSignInUserSession()?.getIdToken().payload.email_verified) {
				Auth.signOut();
				return { user: undefined, initialized: true };
			} else if (newUser?.getUsername() != user?.getUsername()) {
				return { user: newUser, initialized: true };
			}
			return { user, initialized: true };
		},
		{ user: undefined, initialized: true }
	);

	useEffect(() => {
		if (user) {
			datadogRum.setUser({ id: user.getUsername() });
		} else if (session) {
			datadogRum.setUser({ id: session.username });
		} else {
			datadogRum.removeUser();
		}
	}, [user, session]);

	useEffect(() => {
		if (user) {
			configCat.setDefaultUser(
				new ConfigCatUser(
					user.getUsername(),
					user.getSignInUserSession()?.getIdToken()?.payload?.email
				)
			);
		} else if (session) {
			configCat.setDefaultUser(new ConfigCatUser(session.username, session.email));
		} else {
			configCat.clearDefaultUser();
		}
	}, [configCat, user, session]);

	return (
		<AuthContext.Provider
			value={{ user, updateUser, initialized, session, updateSession: commitSession }}>
			{children}
		</AuthContext.Provider>
	);
};

export type SignInErrorTypes =
	| 'NOT_AUTHORIZED'
	| 'PASSWORD_RESET_REQUIRED'
	| 'ALREADY_LOGGED_IN'
	| 'SERVICE_ERROR';

// The error cases from Amplify on sign-in aren't well-documented or easy to use
// so let's wrap them with something easier
export class SignInError extends Error {
	type: SignInErrorTypes;

	constructor(type: SignInErrorTypes, message?: string) {
		super(message ?? `Sign in error: ${type}`);
		this.type = type;
	}
}

const signInApiError = (e: unknown): SignInError => {
	if (e instanceof APIError) {
		if (e.status === 401) {
			return new SignInError('NOT_AUTHORIZED', 'Incorrect email or password.');
		}
		if (e.status === 403) {
			return new SignInError('PASSWORD_RESET_REQUIRED', 'Password reset required.');
		}
	}
	return new SignInError('SERVICE_ERROR', e instanceof Error ? e.message : 'Sign in failed.');
};

export type SignUpErrorTypes =
	| 'INVALID_PASSWORD'
	| 'USER_EXISTS'
	| 'ALREADY_LOGGED_IN'
	| 'SERVICE_ERROR';

export class SignUpError extends Error {
	type: SignUpErrorTypes;

	constructor(type: SignUpErrorTypes, message?: string) {
		super(message ?? `Sign up error: ${type}`);
		this.type = type;
	}
}

export type VerifySignUpErrorTypes =
	| 'WRONG_CODE'
	| 'EXPIRED_CODE'
	| 'ALREADY_LOGGED_IN'
	| 'SERVICE_ERROR';

export class VerifySignUpError extends Error {
	type: VerifySignUpErrorTypes;

	constructor(type: VerifySignUpErrorTypes, message?: string) {
		super(message ?? `Verify sign up error: ${type}`);
		this.type = type;
	}
}

export type ResetPasswordErrorTypes =
	| 'WRONG_CODE'
	| 'EXPIRED_CODE'
	| 'INVALID_PASSWORD'
	| 'ALREADY_LOGGED_IN'
	| 'SERVICE_ERROR';

export class ResetPasswordError extends Error {
	type: ResetPasswordErrorTypes;

	constructor(type: ResetPasswordErrorTypes, message?: string) {
		super(message ?? `Reset password error: ${type}`);
		this.type = type;
	}
}

export type ConfirmPasswordErrorTypes =
	| 'INVALID_PASSWORD'
	| 'NOT_AUTHORIZED'
	| 'PASSWORD_RESET_REQUIRED'
	| 'USER_ERROR'
	| 'SERVICE_ERROR';

export class ConfirmPasswordError extends Error {
	type: ConfirmPasswordErrorTypes;

	constructor(type: ConfirmPasswordErrorTypes, message?: string) {
		super(message ?? `Change password error: ${type}`);
		this.type = type;
	}
}

// Dependency loop break; ApolloProvider needs getAuthToken from AuthProvider,
// but AuthProvider needs ApolloProvider set up.
// So, we provide a version of this function which works for ApolloProvider.
export const getAuthToken = async () => {
	if (!isBrowser) return '';

	const sessionToken = await getSessionAuthToken();
	if (sessionToken !== undefined) return sessionToken;

	try {
		const session = await Auth.currentSession();
		const idToken = session.getIdToken();
		return idToken.getJwtToken();
	} catch (e) {
		console.log(`Error getting auth token: ${e.message}`);
		return '';
	}
};

export const getPhoneNumber = async (user: CognitoUser) => {
	const attributes = await Auth.userAttributes(user);
	const phoneNumber = attributes.find((attr) => attr.getName() === 'phone_number')?.getValue();
	if (phoneNumber === null || phoneNumber === undefined) {
		datadogRum.addError(
			"Couldn't find phone number attribute in user attributes, nor in auth provider "
		);
		throw new Error("Couldn't find phone number attribute in user attributes");
	}
	return phoneNumber;
};

export type MultifactorChallengeName = 'SMS_MFA' | 'EMAIL_OTP' | 'SOFTWARE_TOKEN_MFA';

export enum AuthStep {
	needPassword = 'needPassword', // The user needs to provide a password (and we need to validate it)
	needMultifactorVerification = 'needMultifactorVerification', // The user needs to provide an MFA code sent to their phone or email on file
	optionalMultifactorSetup = 'optionalMultifactorSetup', // The user has the option to set up multifactor now or later
	optionalSMSMultifactorSetupPhone = 'optionalSMSMultifactorSetupPhone', // The user has the option to set up SMS multifactor now or later
	optionalSMSMultifactorSetupVerification = 'optionalSMSMultifactorSetupVerification', // The user needs to provide a SMS multifactor code to complete SMS multifactor setup
	optionalTOTPMultifactorSetupVerification = 'optionalTOTPMultifactorSetupVerification',
	done = 'done',
}

export type AuthStepContext =
	| { nextStep: 'needPassword' }
	| {
			nextStep: 'needMultifactorVerification';
			username: string;
			session: string;
			challengeName: MultifactorChallengeName;
			codeDeliveryDestination?: string;
			codeDeliveryMedium?: string;
	  }
	| { nextStep: 'optionalMultifactorSetup' }
	| { nextStep: 'optionalSMSMultifactorSetupPhone' }
	| { nextStep: 'optionalSMSMultifactorSetupVerification' }
	| { nextStep: 'optionalTOTPMultifactorSetupVerification'; secretCode: string }
	| { nextStep: 'done' };

export const useAuth = () => {
	const { user, updateUser, initialized, session, updateSession } = useContext(AuthContext);

	// Shared by signIn() and verifyMultifactor() - both end with backend-v2
	// handing back a complete token set (or don't, if something's wrong).
	const completeSignIn = (result: SignInResult): AuthStepContext => {
		if (!result.accessToken || !result.idToken || !result.refreshToken || !result.username) {
			throw new SignInError('SERVICE_ERROR', 'Sign in did not return a complete session.');
		}

		const claims = decodeIdTokenClaims(result.idToken);
		if (claims.email_verified === false) {
			throw new SignInError('NOT_AUTHORIZED', 'Email address is not verified.');
		}

		updateSession({
			accessToken: result.accessToken,
			idToken: result.idToken,
			refreshToken: result.refreshToken,
			username: result.username,
			email: claims.email ?? '',
		});

		return { nextStep: AuthStep.done };
	};

	const signIn = async (email: string, password: string): Promise<AuthStepContext> => {
		if (!isBrowser) {
			return { nextStep: AuthStep.done };
		}
		if (user || session) {
			throw new SignInError('ALREADY_LOGGED_IN', "Can't sign in when already signed in.");
		}

		let result;
		try {
			result = await apiSignIn(email, password);
		} catch (e) {
			throw signInApiError(e);
		}

		if (
			result.challengeName === 'SMS_MFA' ||
			result.challengeName === 'EMAIL_OTP' ||
			result.challengeName === 'SOFTWARE_TOKEN_MFA'
		) {
			if (!result.session || !result.username) {
				throw new SignInError('SERVICE_ERROR', 'Sign in did not return a complete MFA session.');
			}
			return {
				nextStep: AuthStep.needMultifactorVerification,
				username: result.username,
				session: result.session,
				challengeName: result.challengeName,
				codeDeliveryDestination: result.codeDeliveryDestination,
				codeDeliveryMedium: result.codeDeliveryMedium,
			};
		}

		if (result.challengeName) {
			throw new SignInError(
				'SERVICE_ERROR',
				`Sign in requires additional verification (${result.challengeName}), which isn't supported yet.`
			);
		}

		completeSignIn(result);
		// No MFA challenge on this sign-in means the member doesn't have MFA
		// enrolled yet - offer to set it up rather than going straight to done.
		// (Someone who already has MFA enabled hits the challenge branch above
		// instead and never reaches this.)
		return { nextStep: AuthStep.optionalMultifactorSetup };
	};

	// Completes the SMS_MFA / EMAIL_OTP challenge signIn() returns for members
	// who already have MFA enabled. backend-v2 collapses every failure reason
	// (wrong code, expired code, expired session) into a single 401, so we
	// can't distinguish them the way the old Cognito-direct flow could.
	const verifyMultifactor = async (
		username: string,
		mfaSession: string,
		challengeName: MultifactorChallengeName,
		code: string
	): Promise<AuthStepContext> => {
		let result;
		try {
			result = await apiVerifyMfa(username, mfaSession, challengeName, code);
		} catch (e) {
			if (e instanceof APIError) {
				throw new SignInError('NOT_AUTHORIZED', 'Incorrect or expired code. Please try again.');
			}
			throw new SignInError(
				'SERVICE_ERROR',
				e instanceof Error ? e.message : 'Verification failed.'
			);
		}

		return completeSignIn(result);
	};

	const requireEnrollmentAuth = async (): Promise<{ authToken: string; accessToken: string }> => {
		if (!session) {
			throw new Error("Can't set up multi-factor authentication without an active session.");
		}
		const authToken = await getSessionAuthToken();
		if (!authToken) {
			throw new Error('Unable to authenticate this request.');
		}
		return { authToken, accessToken: session.accessToken };
	};

	const updatePhoneNumber = async (newPhoneNumber: string): Promise<AuthStepContext> => {
		const { authToken, accessToken } = await requireEnrollmentAuth();
		try {
			await apiEnrollSmsMfa(authToken, accessToken, newPhoneNumber);
		} catch (e) {
			console.error('Error updating phone number', e);
			throw e;
		}
		return { nextStep: AuthStep.optionalSMSMultifactorSetupVerification };
	};

	const confirmUpdatedPhoneNumberAndEnroll = async (code: string): Promise<AuthStepContext> => {
		const { authToken, accessToken } = await requireEnrollmentAuth();
		try {
			await apiConfirmEnrollSmsMfa(authToken, accessToken, code);
		} catch (e) {
			console.error('Error confirming updated phone number and enrolling in MFA', e);
			if (e instanceof APIError) {
				throw new Error('Incorrect or expired code. Please try again.');
			}
			throw e;
		}
		return { nextStep: AuthStep.done };
	};

	const enableEmailMfa = async (): Promise<AuthStepContext> => {
		const { authToken, accessToken } = await requireEnrollmentAuth();
		try {
			await apiEnableEmailMfa(authToken, accessToken);
		} catch (e) {
			console.error('Error enabling email MFA', e);
			throw e;
		}
		return { nextStep: AuthStep.done };
	};

	const beginEnrollTotpMfa = async (): Promise<AuthStepContext> => {
		const { authToken, accessToken } = await requireEnrollmentAuth();
		let result;
		try {
			result = await apiEnrollTotpMfa(authToken, accessToken);
		} catch (e) {
			console.error('Error beginning TOTP enrollment', e);
			throw e;
		}
		return {
			nextStep: AuthStep.optionalTOTPMultifactorSetupVerification,
			secretCode: result.secretCode,
		};
	};

	const confirmEnrollTotpMfa = async (code: string): Promise<AuthStepContext> => {
		const { authToken, accessToken } = await requireEnrollmentAuth();
		try {
			await apiConfirmEnrollTotpMfa(authToken, accessToken, code);
		} catch (e) {
			console.error('Error confirming TOTP enrollment', e);
			if (e instanceof APIError) {
				throw new Error('Incorrect or expired code. Please try again.');
			}
			throw e;
		}
		return { nextStep: AuthStep.done };
	};

	const signOut = async () => {
		if (!isBrowser) return;
		if (user) {
			await Auth.signOut();
		}
		if (session) {
			try {
				await apiSignOut(session.refreshToken);
			} catch (e) {
				console.error('Error revoking session server-side', e);
			}
		}
		updateSession(undefined);
		if (window.localStorage.getItem('prep')) {
			window.localStorage.removeItem('prep');
			window.localStorage.removeItem('prep_step');
		}
		updateUser(undefined);
	};

	const signUp = async (email: string) => {
		if (user) {
			throw new SignUpError('ALREADY_LOGGED_IN', "Can't sign up when already logged in.");
		}

		try {
			await axios.post(
				`${ApiEndpoints.public!}/signup`,
				{
					email,
				},
				{
					headers: {
						'Content-Type': 'application/json',
						Accept: 'application/json',
					},
				}
			);
		} catch (e) {
			if ('code' in e) {
				switch (e.code) {
					case 'InvalidPasswordException':
						throw new SignUpError('INVALID_PASSWORD', e.message);
					case 'UserLambdaValidationException':
						if (e.message.match(/ERROR_DUPLICATE_EMAIL_FOUND/)) {
							throw new SignUpError('USER_EXISTS');
						} else {
							throw new SignUpError('SERVICE_ERROR', e.message);
						}
					default:
						throw new SignUpError('SERVICE_ERROR', e.message);
				}
			} else {
				throw e;
			}
		}
	};

	const verifySignUp = async (
		email: string,
		password: string,
		code: string,
		state: string,
		firstName?: string,
		lastName?: string
	) => {
		if (user) {
			throw new VerifySignUpError(
				'ALREADY_LOGGED_IN',
				"Can't verify signup when already logged in."
			);
		}

		let userid = '';

		try {
			const response = await axios.post(
				`${ApiEndpoints.public!}/signup-verify-code`,
				{
					email,
					code,
					password,
					state,
					firstName,
					lastName,
				},
				{
					headers: {
						'Content-Type': 'application/json',
						Accept: 'application/json',
					},
				}
			);
			userid = response.data.userid;
		} catch (e) {
			throw new VerifySignUpError('SERVICE_ERROR', e.message);
		}
		console.log('Code verification successful.');
		await signIn(userid, password);
	};

	const requestPasswordReset = async (email: string) => {
		if (user) throw new Error("Can't reset password when signed in.");
		await apiBeginResetPassword(email);
	};

	const resetPassword = async (email: string, code: string, password: string) => {
		if (user)
			throw new ResetPasswordError('ALREADY_LOGGED_IN', "Can't reset password when signed in.");

		try {
			await apiCompleteResetPassword(email, code, password);
		} catch (e) {
			if (e instanceof APIError) {
				throw new ResetPasswordError('WRONG_CODE', 'Incorrect or expired code. Please try again.');
			}
			throw e;
		}
	};

	const providerGetAuthToken = async () => {
		const sessionToken = await getSessionAuthToken();
		if (sessionToken !== undefined) return sessionToken;
		if (user === undefined) return '';
		return await getAuthToken();
	};

	const confirmPassword = async (email: string, oldPassword: string, newPassword: string) => {
		try {
			if (!email || !oldPassword || !newPassword) {
				throw new ConfirmPasswordError('SERVICE_ERROR', 'All parameters are required.');
			}
			const user = await Auth.signIn(email, oldPassword);
			if (!user.username) {
				throw new ConfirmPasswordError('SERVICE_ERROR', 'Did not receive username');
			}
			if (user.challengeName === 'NEW_PASSWORD_REQUIRED') {
				await Auth.completeNewPassword(user, newPassword);
				updateUser(await Auth.currentAuthenticatedUser());
				await signIn(email, newPassword);
			}
		} catch (e: any) {
			if ('code' in e) {
				switch (e.code) {
					case 'InvalidParameterException':
						throw new ConfirmPasswordError('INVALID_PASSWORD', e.message);
					case 'InvalidPasswordException':
						throw new ConfirmPasswordError('INVALID_PASSWORD', e.message);
					case 'NotAuthorizedException':
						throw new ConfirmPasswordError('NOT_AUTHORIZED', e.message);
					case 'PasswordResetRequiredException':
						throw new ConfirmPasswordError('PASSWORD_RESET_REQUIRED', e.message);
					case 'UserNotConfirmedException':
						throw new ConfirmPasswordError('USER_ERROR', e.message);
					case 'UserNotFoundException':
						throw new ConfirmPasswordError('USER_ERROR', e.message);
					case 'UserLambdaValidationException':
						throw e;
					default:
						throw new ConfirmPasswordError('SERVICE_ERROR', e.message);
				}
			}
		}
	};

	return {
		user: user as User,
		userId: (user?.getUsername() ?? session?.username ?? '') as string,
		email: (user?.getSignInUserSession()?.getIdToken().payload.email ??
			session?.email ??
			'') as string,
		loggedIn: !!user || !!session,
		initialized,
		signIn,
		verifyMultifactor,
		updatePhoneNumber,
		confirmUpdatedPhoneNumberAndEnroll,
		enableEmailMfa,
		beginEnrollTotpMfa,
		confirmEnrollTotpMfa,
		signOut,
		signUp,
		verifySignUp,
		requestPasswordReset,
		resetPassword,
		confirmPassword,
		getAuthToken: providerGetAuthToken,
	};
};
